/***********************/
/* Angular + external */
import { Component, OnDestroy, Input } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subscription, Subject, Observable } from "rxjs";
/***********************/
/* BFX */
// models
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BfxApplicationNotificationCommand } from "./models/bfx-application-notification-command.enum";
// components
import { BfxDialogBaseComponent } from "../bfx-dialogs/bfx-dialog-base.component";

// services
import { BfxConfigService } from "../../shared/auth-services/system-config/bfx-config.service";
import { BfxApplicationNotificationData } from "./models/bfx-application-notification-data.model";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopAppNotification } from "../bfx-native-desktop/native-desktop-app-notification";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { BFXAppNotificationType } from "shared/cubiq-services";

const TIME_BOUNDARIES: any[] = [
  {
    time: 2 * 24 * 60 * 60,
    message: "notification.app.days.ago",
    divider: 24 * 60 * 60,
  },
  {
    time: 24 * 60 * 60,
    message: "notification.app.day.ago",
    divider: 24 * 60 * 60,
  },
  {
    time: 2 * 60 * 60,
    message: "notification.app.hours.ago",
    divider: 60 * 60,
  },
  { time: 60 * 60, message: "notification.app.hour.ago", divider: 60 * 60 },
  { time: 2 * 60, message: "notification.app.mins.ago", divider: 60 },
  { time: 60, message: "notification.app.min.ago", divider: 60 },
];

const ICON_INFO = "icon_ion_info_filled";
const ICON_WARNING = "icon_ion_warning_filled";

@Component({
  selector: `bfx-application-notification`,
  templateUrl: `./html/bfx-application-notification.html`,
  standalone: false,
})
export class BfxApplicationNotificationComponent implements OnDestroy {
  /***************************************/
  /* DATA */
  private _data: BfxApplicationNotificationData;
  public relativeTimeString: string = "Just now";
  private intervalSubscription: any = null;

  public notificationOpen: boolean = false;

  // Declare default class name for View button
  public toggleOrdersView = "fa icon-ion_chevron_down";

  /** Icon class - determines the icon displayed */
  public iconClass: string = ICON_INFO;
  /** Notification class - full presentation style */
  public notificationClass: string = "bfx-notification-info-bold-inline";

  /** Classes for notification buttons */
  public readonly buttonClasses =
    "bfx-button-secondary-moderate-unsized bfx-button-sm bfx-notification-label";

  @Input()
  public get data(): BfxApplicationNotificationData {
    return this._data;
  }
  public set data(value: BfxApplicationNotificationData) {
    this._data = value;
    this.setupIconClass();
    if (this.data?.time) {
      this.relativeTimeString = this.relativeTime;
    }
  }

  /**********************************************************************/
  /* CONSTRUCTOR  + LIFE CYCLE HOOKS */
  constructor(private _translationService: TranslateService) {
    this.initialize();
  }

  ngOnDestroy() {
    this.clearInterval();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  public initialize(): void {
    this.intervalSubscription = setInterval(() => {
      if (this.data?.time) {
        this.relativeTimeString = this.relativeTime;
      }
    }, 5000);
  }

  private clearInterval(): void {
    if (this.intervalSubscription != null) {
      clearInterval(this.intervalSubscription);
    }
  }

  public get relativeTime(): string {
    let currentTime: number = new Date().getTime() / 1000;
    let notificationTime: number = this.data.time / 1000;

    let relativeTime: string = null;
    let time: number = 0;
    let delta: number = currentTime - notificationTime;

    for (let i: number = 0; i < TIME_BOUNDARIES.length; i++) {
      let obj: any = TIME_BOUNDARIES[i];
      if (delta > obj.time) {
        time = Math.floor(delta / obj.divider);
        relativeTime = obj.message;
        break;
      }
    }

    return this._translationService.instant(
      relativeTime != null ? relativeTime : "notification.app.just.now",
      [time],
    );
  }

  public action(command: BfxApplicationNotificationCommand, itemId?: string): void {
    this.data.doAction(command, itemId);
  }

  close() {
    this.data.doAction(BfxApplicationNotificationCommand.POPUP_CLOSE);
    this.clearInterval();
  }

  private setupIconClass(): void {
    if (this._data) {
      switch (this._data.type) {
        case BFXAppNotificationType.APP_BMARK_NEW_ORDERS:
        case BFXAppNotificationType.APP_BMARK_CANCELLED:
        case BFXAppNotificationType.APP_BMARK_MODIFIED:
        case BFXAppNotificationType.APP_BMARK_FILLED:
        case BFXAppNotificationType.APP_BMARK_ENTERED:
        case BFXAppNotificationType.ACTIVATED:
        case BFXAppNotificationType.CANCELLED:
        case BFXAppNotificationType.EXPIRED:
        case BFXAppNotificationType.PART_FILLED:
        case BFXAppNotificationType.FILLED:
        case BFXAppNotificationType.MODIFIED:
        case BFXAppNotificationType.SUSPENDED:
        case BFXAppNotificationType.ENTERED:
          // Neutral notifications
          this.iconClass = ICON_INFO;
          this.notificationClass = "bfx-notification-neutral";
          break;
        case BFXAppNotificationType.APP_BMARK_UNSOLICITED_CANCEL:
          // Subtle Warn
          this.iconClass = ICON_WARNING;
          this.notificationClass = "bfx-notification-info";
          break;
        default:
          // None of the above are supported by the spec
          logger.out.error("Unsupported notification type - " + this._data.type);
      }
    }
  }
}
